import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AdminGate } from "@/components/AdminGate";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  getIndexingDashboard,
  setIndexingStatus,
} from "@/lib/page-performance.functions";
import { HEALTH_META, pageHealth, type HealthTier } from "@/lib/linked-pages";
import { getThinPageStats } from "@/lib/thin-pages.functions";

export const Route = createFileRoute("/admin/indexing")({
  head: () => ({
    meta: [
      { title: "Indexing Review — Proven Pages | AutoSpecData" },
      {
        name: "description",
        content:
          "Internal review screen for the Search Console data that decides which vehicle pages stay indexed.",
      },
      { property: "og:title", content: "Indexing Review — Proven Pages | AutoSpecData" },
      {
        property: "og:description",
        content:
          "Internal review screen for the Search Console data that decides which vehicle pages stay indexed.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  ssr: false,
  component: IndexingPage,
});

const num = (n: number | null | undefined) =>
  n == null ? "—" : Math.round(n * 10) / 10;

function HealthChip({ tier, count }: { tier: HealthTier; count?: number }) {
  const meta = HEALTH_META[tier] ?? HEALTH_META.nodata;
  return (
    <span
      title={meta.hint}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.chip}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
      {count != null && <span className="opacity-70">{count}</span>}
    </span>
  );
}

/** Green/amber bar showing how much of a group is actually performing. */
function HealthBar({ breakdown, total }: { breakdown: any; total: number }) {
  const order: HealthTier[] = ["winning", "climbing", "waiting", "stalled", "nodata"];
  const safeTotal = Math.max(total, 1);
  return (
    <span className="flex h-1.5 w-28 overflow-hidden rounded-full bg-muted">
      {order.map((t) => {
        const v = breakdown?.[t] ?? 0;
        if (!v) return null;
        return (
          <span
            key={t}
            title={`${HEALTH_META[t].label}: ${v}`}
            className={HEALTH_META[t].dot}
            style={{ width: `${(v / safeTotal) * 100}%` }}
          />
        );
      })}
    </span>
  );
}

function StatusButtons({
  path,
  status,
  onDone,
}: {
  path: string;
  status: string;
  onDone: () => void;
}) {
  const mutate = useServerFn(setIndexingStatus);
  const [busy, setBusy] = useState(false);
  const apply = async (next: "auto" | "pinned" | "retired") => {
    setBusy(true);
    await mutate({ data: { path, status: next } });
    setBusy(false);
    onDone();
  };
  const cls = (active: boolean) =>
    `rounded px-2 py-0.5 text-xs border ${
      active
        ? "border-primary bg-primary/15 text-primary"
        : "border-border text-muted-foreground hover:text-foreground"
    }`;
  return (
    <span className="inline-flex gap-1" aria-busy={busy}>
      <button type="button" disabled={busy} className={cls(status === "pinned")} onClick={() => apply("pinned")}>
        Pin
      </button>
      <button type="button" disabled={busy} className={cls(status === "auto")} onClick={() => apply("auto")}>
        Auto
      </button>
      <button type="button" disabled={busy} className={cls(status === "retired")} onClick={() => apply("retired")}>
        Retire
      </button>
    </span>
  );
}

function IndexingPage() {
  return (
    <AdminGate>
      <IndexingLoader />
    </AdminGate>
  );
}

function IndexingLoader() {
  const fetchDashboard = useServerFn(getIndexingDashboard);
  const fetchThin = useServerFn(getThinPageStats);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ["admin", "indexing-dashboard"],
    queryFn: async () => {
      const [dashboard, thinPages] = await Promise.all([fetchDashboard(), fetchThin()]);
      return { ...dashboard, thinPages };
    },
  });
  if (isPending) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  if (error || !data)
    return <p className="p-8 text-sm text-destructive">Could not load the dashboard.</p>;
  return <IndexingDashboard data={data} refresh={() => void refetch()} />;
}

function IndexingDashboard({ data, refresh }: { data: any; refresh: () => void }) {
  const { totals, thresholds, proven, nearMiss, runs, groups, thinPages } = data;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <nav className="text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Indexing review</span>
      </nav>

      <h1 className="mt-4 text-3xl font-bold text-foreground">Indexing Review</h1>
      <p className="mt-3 max-w-3xl text-muted-foreground">
        A page overrides the indexing heuristic when it has earned at least one
        click, or when it has {thresholds.minImpressions}+ impressions at an
        average position of {thresholds.maxPosition} or better. Peaks are kept
        forever, so a proven page never silently loses its slot. Pin or retire
        any page below to override that instantly.
      </p>

      <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-6">
        {[
          ["Tracked", totals.tracked],
          ["Proven", totals.proven],
          ["By clicks", totals.byClicks],
          ["By ranking", totals.byRanking],
          ["Pinned", totals.pinned],
          ["Retired", totals.retired],
        ].map(([label, value]) => (
          <div key={label as string} className="rounded-lg border border-border bg-card p-3">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="mt-1 text-2xl font-semibold text-foreground">
              {(value as number).toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>

      <section className="mt-6 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-foreground">
            Thin attribute pages removed
          </h2>
          <span className="text-xs text-muted-foreground">
            Floor: {thinPages.floor}+ data rows per attribute page
          </span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Auto-generated dimension / safety / EV pages with fewer rows than the
          floor are noindexed and dropped from the sitemap. Pages Search Console
          already rewards keep their slot regardless.
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Generated", thinPages.total],
            ["Kept indexable", thinPages.kept],
            ["Dropped as thin", thinPages.thin],
            ["Rescued by data", thinPages.rescuedByPerformance],
          ].map(([label, value]) => (
            <div key={label as string} className="rounded-md border border-border p-3">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {label}
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-foreground">
                {(value as number).toLocaleString()}
              </dd>
            </div>
          ))}
        </dl>
        <ul className="mt-3 flex flex-wrap gap-2 text-xs">
          {thinPages.byDataType.map((d: any) => (
            <li
              key={d.dataType}
              className={`rounded-full border px-2 py-0.5 ${
                d.thin === 0
                  ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                  : "border-amber-500/40 text-amber-600 dark:text-amber-400"
              }`}
            >
              {d.dataType}: {d.thin} thin / {d.total}
            </li>
          ))}
        </ul>
      </section>

      <h2 className="mt-10 text-xl font-semibold text-foreground">Linked pages</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The internal-link surfaces we built on purpose, each with the search
        terms it is meant to win. Numbers are the latest weekly snapshot with
        all-time peaks in brackets. The colour tells you how it is going.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {(["winning", "climbing", "waiting", "stalled", "nodata"] as HealthTier[]).map(
          (t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <HealthChip tier={t} />
              <span className="text-xs text-muted-foreground">
                {HEALTH_META[t].hint}
              </span>
            </span>
          ),
        )}
      </div>
      <div className="mt-3 space-y-2">
        {groups.map((g: any) => (
          <LinkedGroup key={g.key} group={g} onDone={refresh} />
        ))}
      </div>

      <h2 className="mt-10 text-xl font-semibold text-foreground">Recent syncs</h2>
      <div className="mt-3 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr>
              <th className="p-2">Date</th>
              <th className="p-2">Received</th>
              <th className="p-2">Written</th>
              <th className="p-2">Previous</th>
              <th className="p-2">Accepted</th>
              <th className="p-2">Note</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-3 text-muted-foreground">
                  No sync has run since history was enabled.
                </td>
              </tr>
            ) : (
              runs.map((r: any, i: number) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-2">{r.captured_on}</td>
                  <td className="p-2">{r.rows_received}</td>
                  <td className="p-2">{r.rows_written}</td>
                  <td className="p-2">{r.previous_rows}</td>
                  <td className="p-2">{r.accepted ? "Yes" : "No"}</td>
                  <td className="p-2 text-muted-foreground">{r.note}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 text-xl font-semibold text-foreground">
        Proven pages ({totals.proven.toLocaleString()})
      </h2>
      <PageTable rows={proven} onDone={refresh} />

      <h2 className="mt-10 text-xl font-semibold text-foreground">
        Near misses ({nearMiss.length})
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Close to the bar but not over it. Pin one to promote it deliberately.
      </p>
      <PageTable rows={nearMiss} onDone={refresh} />
    </div>
  );
}

function PageTable({ rows, onDone }: { rows: any[]; onDone: () => void }) {
  return <PageTableInner rows={rows} onDone={onDone} />;
}

function LinkedGroup({ group, onDone }: { group: any; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const ctr =
    group.impressions > 0
      ? Math.round((group.clicks / group.impressions) * 1000) / 10
      : null;
  return (
    <div
      className={`rounded-lg border bg-card ${
        group.health === "winning"
          ? "border-emerald-500/40"
          : group.health === "stalled"
            ? "border-rose-500/40"
            : "border-border"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-wrap items-center gap-x-6 gap-y-1 p-3 text-left"
        aria-expanded={open}
      >
        <span className="min-w-40 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-foreground">
              {open ? "▾" : "▸"} {group.label}
            </span>
            <HealthChip tier={group.health} />
            <HealthBar breakdown={group.breakdown} total={group.total} />
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {group.goal ?? group.blurb}
          </span>
        </span>
        <span className="text-sm text-muted-foreground">
          {group.withData}/{group.total} with data
        </span>
        <span className="text-sm text-muted-foreground">
          {group.clicks} ({group.peakClicks}) clicks
        </span>
        <span className="text-sm text-muted-foreground">
          {group.impressions.toLocaleString()} (
          {group.peakImpressions.toLocaleString()}) impr.
        </span>
        {ctr != null && (
          <span className="text-sm text-muted-foreground">{ctr}% CTR</span>
        )}
        <span className="text-sm text-muted-foreground">
          best pos. {num(group.bestPosition)}
        </span>
        <span className="text-sm text-foreground">{group.proven} proven</span>
      </button>
      {open && (
        <div className="border-t border-border p-3 pt-0">
          {group.targets?.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Targeting
              </span>
              {group.targets.map((t: string) => (
                <span
                  key={t}
                  className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-foreground"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {(["winning", "climbing", "waiting", "stalled", "nodata"] as HealthTier[])
              .filter((t) => (group.breakdown?.[t] ?? 0) > 0)
              .map((t) => (
                <HealthChip key={t} tier={t} count={group.breakdown[t]} />
              ))}
          </div>
          <PageTableInner rows={group.members} onDone={onDone} />
        </div>
      )}
    </div>
  );
}

function PageTableInner({ rows, onDone }: { rows: any[]; onDone: () => void }) {
  if (rows.length === 0) {
    return (
      <p className="mt-3 rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
        Nothing here yet — the first weekly sync will populate this.
      </p>
    );
  }
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-muted-foreground">
          <tr>
            <th className="p-2">Page</th>
            <th className="p-2">Health</th>
            <th className="p-2">Clicks (peak)</th>
            <th className="p-2">Impr. (peak)</th>
            <th className="p-2">Best pos.</th>
            <th className="p-2">Latest</th>
            <th className="p-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.path}
              className={`border-t border-border ${
                (r.health ?? pageHealth(r)) === "winning" ? "bg-emerald-500/5" : ""
              }`}
            >
              <td className="p-2">
                <a
                  href={r.path}
                  className="text-primary hover:underline"
                  rel="noopener noreferrer"
                >
                  {r.path}
                </a>
              </td>
              <td className="p-2">
                <HealthChip tier={(r.health ?? pageHealth(r)) as HealthTier} />
              </td>
              {r.noData ? (
                <td className="p-2 text-muted-foreground" colSpan={4}>
                  No Search Console data yet
                </td>
              ) : (
                <>
              <td className="p-2">
                {r.clicks} ({r.peak_clicks})
              </td>
              <td className="p-2">
                {r.impressions} ({r.peak_impressions})
              </td>
              <td className="p-2">{num(r.best_position ?? r.position)}</td>
              <td className="p-2 text-muted-foreground">{r.captured_on}</td>
                </>
              )}
              <td className="p-2">
                <StatusButtons path={r.path} status={r.status} onDone={onDone} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
